<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButtonBack href="/report" :query="{ domain: domain }" />
            <ShareReport
              :report="report"
              :filter="baseFilter"
              :dropdownItems="itemDownloadDropdown"
              :labelGraph="getActiveDataSet"
              :allColumn="allColumn"
              @doShare="downloadReport"
            />
            <DownloadReportDropdown
              :report="report"
              :items="itemDownloadDropdown"
              :actualTotal="actualTotal"
            />
          </div>
        </template>
      </PageHeader>
    </div>
    <div class="d-flex align-items-center gap-2 me-2 absolute-right">
      <FavoriteReportButton
        v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
        :report="report"
      />
      <ModalSavedReport
        v-if="hasAccess(['saved-reports:read']) && !report.isSaved"
        :filter="filterValue"
        :detail="filterDetail"
        :report="report"
      />
      <McButton
        variant="secondary"
        :text="$t('report.title.reportParameter')"
        icon="icon-outline_filter"
        @click="showModal"
      />
    </div>
    <b-card :key="summaryKey" class="mb-2 mc-general__label" style="margin-top: 80px">
      <h5 class="mc-general__label">
        {{ $t('report.fields.dataSummary') }}
      </h5>
      <b-row>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="periodReport_label"
            :label="$t('report.object.reportPeriod')"
            label-for="reportPeriod"
          >
            <span v-if="!isBusy">
              {{ periodeLabel }}
            </span>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="licensePlate_label"
            :label="$t('report.fields.licensePlate')"
            label-for="licensePlate"
          >
            <div v-if="!isBusy" class="d-flex align-items-center gap-2">
              <span>{{ licensePlateLabel }}</span>
              <McBadge
                v-if="vehicleIdCount > 2"
                :text="`+${vehicleIdCount - 2}`"
                variant="success"
                size="small"
                type="pill"
              />
            </div>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
        <b-col v-if="displaySummaryChart" class="pt-2" sm="14" md="2">
          <b-form-group
            id="literConsumption"
            :label="$t('report.fields.literConsumption')"
            label-for="literConsumption"
          >
            <div v-if="!isBusySummary" class="d-flex align-items-center gap-2">
              <span>{{
                summary.totalFuelConsumption === null ? '-' : summary.totalFuelConsumption
              }}</span>
            </div>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
        <b-col v-if="displaySummaryChart" class="pt-2" sm="14" md="2">
          <b-form-group
            id="averageLiterPerHour"
            :label="$t('report.fields.averageLiterPerHour')"
            label-for="averageLiterPerHour"
          >
            <div v-if="!isBusySummary" class="d-flex align-items-center gap-2">
              <span>{{
                summary.averageFuelConsumption === null ? '-' : summary.averageFuelConsumption
              }}</span>
            </div>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
        <b-col v-if="displaySummaryChart" class="pt-2" sm="14" md="2">
          <b-form-group
            id="fuelRefill"
            :label="$t('report.fields.fuelRefill')"
            label-for="fuelRefill"
          >
            <div v-if="!isBusySummary" class="d-flex align-items-center gap-2">
              <span>{{ summary.countFuelRefill }}</span>
            </div>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
        <b-col v-if="displaySummaryChart" class="pt-2" sm="14" md="2">
          <b-form-group
            id="literRefill"
            :label="$t('report.fields.literRefill')"
            label-for="literRefill"
          >
            <div v-if="!isBusySummary" class="d-flex align-items-center gap-2">
              <span>{{ summary.totalFuelRefill === null ? '-' : summary.totalFuelRefill }}</span>
            </div>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row v-if="displaySummaryChart">
        <b-col class="pt-2" sm="14" md="4" />
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="fuelAnomaly"
            :label="$t('report.fields.fuelAnomaly')"
            label-for="fuelAnomaly"
          >
            <div v-if="!isBusySummary" class="d-flex align-items-center gap-2">
              <span>{{ summary.countAnomalyFuelLoss }}</span>
            </div>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group id="literLoss" :label="$t('report.fields.literLoss')" label-for="literLoss">
            <div v-if="!isBusySummary" class="d-flex align-items-center gap-2">
              <span>{{
                summary.totalAnomalyFuelLoss === null ? '-' : summary.totalAnomalyFuelLoss
              }}</span>
            </div>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
        <b-col v-show="false" class="pt-2" sm="14" md="2">
          <b-form-group
            id="averageSpeed"
            :label="$t('report.fields.averageSpeed')"
            label-for="averageSpeed"
          >
            <div v-if="!isBusySummary" class="d-flex align-items-center gap-2">
              <span>{{ summary.averageSpeed }}</span>
            </div>
            <b-skeleton v-else width="70%" />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <AlertReportTooMany v-if="actualTotal > totalRows" />
    <McGrapqlTable
      v-if="Object.keys(filters).length > 0"
      :key="tableKey"
      bordered
      withCard
      :searchable="false"
      :fields="fields"
      :apiClient="apiClient"
      :hasCheckbox="false"
      :lazyLoadAddress="columns.includes('address')"
      reportKey="fuel"
      :hasGraph="isShowGraph ?? true"
      @handleTableChanged="handleTableChanged"
    >
      <template #viewManager="{ handleSetDataFields, dataColumns, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataColumns"
          @setColumn="handleSetDataFields"
          @setColumnReport="handleColumnReport"
        />
      </template>
      <template #graphic="{ dataGraph }">
        <b-skeleton-wrapper :loading="dataGraph.isLoadingGraph ?? true">
          <template #loading>
            <b-skeleton-img no-aspect height="400px" />
          </template>
          <LineReport
            :v-if="!dataGraph.isLoadingGraph"
            :labels="dataGraph.chartData?.labels ?? []"
            :datasets="dataGraph.chartData?.datasets ?? []"
            :options="fuelChartOptions"
            y-label="Volume"
            :y-axes-additional="dataGraph.yAxesAdditional ?? yAxesAdditional"
          />
        </b-skeleton-wrapper>
      </template>
      <template #cell(createdOn)="data">
        {{ momentDateShortMonth(data.item.createdOn) }}<br />
        <span class="text-muted">{{ momentTimeSeconds(data.item.createdOn, true) }}</span>
      </template>
      <template #cell(vehicle)="data">
        {{ data.item.vehicle.licensePlate ? data.item.vehicle.licensePlate : '-' }}
      </template>

      <template #cell(vehicleType)="data">
        {{
          data.item.vehicle
            ? data.item.vehicle.vehicleType
              ? data.item.vehicle.vehicleType.manufacturer
              : '-'
            : '-'
        }}
        /
        {{
          data.item.vehicle
            ? data.item.vehicle.vehicleType
              ? data.item.vehicle.vehicleType.model
              : '-'
            : '-'
        }}
        /
        {{
          data.item.vehicle
            ? data.item.vehicle.vehicleType
              ? data.item.vehicle.vehicleType.type
              : '-'
            : '-'
        }}
      </template>
      <template #cell(acc)="data">
        {{ data.item.acc == 'on' ? $t('report.label.on') : $t('report.label.off') }}
      </template>
      <template #cell(fuel)="data">{{ getFuelValue(data.item) }} </template>
      <template #cell(fuelPercentage)="data">{{ getFuelPercentage(data.item) }} </template>
      <template #cell(address)="data">
        <b-skeleton v-if="data.item.address == '' || data.item.address == null" width="70%" />
        <AddressValue
          v-else
          :id="`address-${data.index}`"
          :latitude="data.item.latitude"
          :longitude="data.item.longitude"
          :address="data.item.address"
        />
      </template>
      <template #cell(sensorType)="data">
        {{ getFuelSensor(data.item.sensors) }}
      </template>
    </McGrapqlTable>
    <ModalFilter
      v-if="isMounted"
      ref="modal-filter"
      :report="report"
      :unRedirect="true"
      @refresh-table="forceRerender"
    />
    <McReportDownloader />
    <ModalSavedReportVerification
      :key="modalVerifReport"
      :domain="domain"
      :message="message"
      :error="isError"
    />
    <ModalEmail
      v-if="actualTotal > totalRows"
      :id="idEmailModal"
      :recipients="recipientsEmail"
      @submit-emails="handleEmails"
    />
  </Layout>
</template>
<script>
import appConfig from '@/app.config'
import LineReport from '@/components/Chart/LineReport.vue'
import McBadge from '@/components/McBadge'
import McButton from '@/components/McButton'
import McButtonBack from '@/components/McButtonBack'
import McFilterSelect from '@/components/McFilterSelect'
import McGrapqlTable from '@/components/McGrapqlTable'
import McReportDownloader from '@/components/McReportDownloader'
import PageHeader from '@/components/page-header-v2'
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import { QueryCache } from '@/mixin/query-cache'
import responsive from '@/mixin/responsive'
import Layout from '@/router/layouts/main'
import DownloadReportDropdown from '@/router/views/report/components/DownloadReportDropdown'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import ModalEmail from '@/router/views/report/components/ModalEmail'
import ModalFilter from '@/router/views/report/components/ModalFilter'
import ModalSavedReport from '@/router/views/report/components/ModalSavedReport'
import ModalSavedReportVerification from '@/router/views/report/components/ModalSavedReportVerification'
import {
  FUEL_REPORT,
  GET_REPORT_FUEL,
  GET_REPORT_FUEL_PAGINATED,
} from '@/services/api/report/report'
import moment from 'moment'
import { mapGetters } from 'vuex'
import AddressValue from '../../components/AddressValue.vue'
import AlertReportTooMany from '../../components/AlertReportTooMany.vue'
import ShareReport from '../../components/ShareReport.vue'
import reportList from '../../lib/reportList.js'
import fields from './lib/fields/fields'
import { buildFuelRangeAxes } from './lib/helper/fuel-chart-axis'
import {
  formatFuelTooltip,
  getFuelSensorString,
  normalizeFuelValues,
} from './lib/helper/fuel-helper'
moment.locale('id')

export default {
  page: {
    title: 'Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McGrapqlTable,
    McFilterSelect,
    McReportDownloader,
    McButton,
    ModalFilter,
    LineReport,
    ModalSavedReport,
    ModalSavedReportVerification,
    FavoriteReportButton,
    DownloadReportDropdown,
    ModalEmail,
    McBadge,
    AlertReportTooMany,
    ShareReport,
    AddressValue,
  },
  mixins: [Helper, MixPanel, responsive],
  data() {
    return {
      isMounted: false,
      series: [
        {
          name: 'Fuel (Liter)',
          data: [84, 94, 104, 71],
        },
        {
          name: 'Fuel (%)',
          data: [56, 66, 76, 43],
        },
        {
          name: 'Speed (Km/h)',
          data: [38, 48, 58, 26],
        },
        {
          name: 'ACC',
          data: [31, 41, 51, 19],
        },
      ],
      chartData: {
        labels: [],
        datasets: [],
      },
      reportFuel: [],
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      totalRows: 1,
      filters: {},
      tableKey: 0,
      isBusy: false,
      reportFields: fields.map((item, i) => ({ ...item, order: i + 1 })),
      reportKey: 'fuel',
      filterDetail: {},
      summaryKey: 50,
      yAxesAdditional: {
        id: 'acc',
        display: true,
        ticks: {
          min: -1,
          max: 2,
          stepSize: 0.5,
          beginAtZero: true,
          callback: function (label) {
            if (label == 1) {
              return 'Nyala'
            } else if (label == 0) {
              return 'Mati'
            }
          },
        },
        gridLines: {
          color: 'rgba(166, 176, 207, 0.1)',
        },
        position: 'right',
        scaleLabel: {
          display: true,
          labelString: 'ACC',
        },
      },
      isLoadingGraph: false,
      queryCache: new QueryCache(),
      message: '',
      isError: false,
      modalVerifReport: 100,
      emails: [],
      recipientsEmail: [],
      isSubmitEmail: false,
      idEmailModal: 'modal-email-fuel',
      reportAfterEmail: '',
      actualTotal: 0,
      summary: {
        totalFuelConsumption: 0,
        averageFuelConsumption: 0,
        countFuelRefill: 0,
        totalFuelRefill: 0,
        countAnomalyFuelLoss: 0,
        totalAnomalyFuelLoss: 0,
        averageSpeed: 0,
      },
      isBusySummary: true,
      fuelRangeAxes: [],
    }
  },
  computed: {
    fuelChartOptions() {
      const options = { tooltips: { callbacks: { label: formatFuelTooltip } } }
      if (!this.fuelRangeAxes.length) return options

      const datasets = this.chartData.datasets
      const axes = [
        ...this.fuelRangeAxes.map((axis) => ({
          ...axis,
          gridLines: { ...axis.gridLines, drawOnChartArea: false },
        })),
        {
          id: 'numeric',
          type: 'linear',
          position: 'left',
          display: false,
          gridLines: { color: 'rgba(166, 176, 207, 0.1)', drawOnChartArea: false },
          scaleLabel: { display: false },
        },
        {
          ...this.yAxesAdditional,
          display: datasets.some((dataset) => dataset.yAxisID === 'acc'),
          gridLines: { ...this.yAxesAdditional.gridLines, drawOnChartArea: false },
        },
      ]
      const gridAxis = axes.find((axis) => axis.display)
      if (gridAxis) gridAxis.gridLines.drawOnChartArea = true
      options.scales = { yAxes: axes }
      return options
    },
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      getFeatureFlagData: 'identity/getFeatureFlagData',
    }),
    domain() {
      return this.report.domain
    },
    title() {
      return this.report.labelName ?? this.$t(this.report.label)
    },
    fields() {
      return this.reportFields
    },
    columns() {
      return this.reportFields
        .filter((item) => item.shown || item.shown === undefined)
        .map((field) => field.header)
    },
    allColumn() {
      return this.reportFields.map((field) => field.header)
    },
    baseFilter() {
      const filter = this.getBaseFilter(this.filters)
      const result = {
        ...filter,
        driver: false,
        column: this.columns,
      }
      if (
        this.getFeatureFlagData('vsms2_report_fuel_version') &&
        this.checkApiVersion(result, this.report.reportKey ?? this.report.key)
      ) {
        result.version = this.getFeatureFlagData('vsms2_report_fuel_version').value
      }
      return result
    },
    generateFilter() {
      const { valid, msg } = this.validateFilter(this.report.filter, this.filters)
      if (valid) {
        const model = this.baseFilter
        return { valid: true, model }
      } else {
        this.$mcToast.error(msg)
        return { valid: false, model: {} }
      }
    },
    filterReport() {
      const { valid, model } = this.generateFilter
      if (!valid) return null
      const filterBody = {
        ...model,
      }
      return filterBody
    },
    isShowGraph() {
      return (
        this.getTotalDayReport(this.filters.periode) <= 7 &&
        this.hasOnlyOneVehicleReport(this.filters.vehicleId)
      )
    },
    displaySummaryChart() {
      return this.filters.summaryChart && this.vehicleIdCount === 1
    },
    apiClient() {
      return {
        get: async (filter) => {
          const model = this.filterReport
          if (!model) return {}
          const self = this
          self.isBusy = true
          const { summaryChart, ...remainingModel } = model
          const filterBody = {
            ...remainingModel,
            column: this.columns.filter((item) => item !== 'address'),
            ...filter,
          }
          const res = await this.queryCache.fetch(
            GET_REPORT_FUEL_PAGINATED,
            {
              filter: filterBody,
            },
            self.reportKey
          )
          const result = res.reportFuelPaginated.reportFuelPaginated
          self.totalRows = result?.pagination?.total ?? 0
          self.isBusy = false
          self.message = res.reportFuelPaginated.message
          self.isError = !res.reportFuelPaginated.isSuccessful
          self.modalVerifReport += 1
          self.actualTotal = result?.pagination?.actualTotal ?? 0
          return result
        },
        getGraph: async () => {
          this.fuelRangeAxes = []
          const { valid, model } = this.generateFilter
          if (!valid) return {}
          const self = this
          self.isBusySummary = true
          let dataLabels = []
          const { summaryChart, ...remainingModel } = model
          const filterBody = {
            ...remainingModel,
            column: this.columns.filter((item) => item !== 'address' && item !== 'geofence'),
            driver: false,
          }
          if (
            this.getFeatureFlagData('vsms2_report_fuel_version') &&
            this.checkApiVersion(filterBody, this.report.reportKey ?? this.report.key)
          ) {
            filterBody.version = this.getFeatureFlagData('vsms2_report_fuel_version').value
          }

          if (!valid || !self.isShowGraph) return {}

          const res = await this.$apollo.query({
            query: GET_REPORT_FUEL,
            variables: {
              filter: filterBody,
            },
            fetchPolicy: 'no-cache',
          })

          self.chartData.datasets = []
          let data = res.data
          const rangeAxes = buildFuelRangeAxes(
            data.reportFuel.reportFuel.data,
            this.columns,
            this.hasAccess(['b1x-new-fuel-expectation:read'])
          )
          self.fuelRangeAxes = rangeAxes.axes
          const summary = data.reportFuel.reportFuel.summary
          this.summary = summary
          self.isBusySummary = false

          dataLabels = data.reportFuel.reportFuel.data.map(
            (item) => `${self.momentDMY_HI(item.createdOn)} ${self.getLabelTimezone()}`
          )
          const lastDate = new Date(this.filters.periode[1])
          lastDate.setHours(23, 59, 59, 0)
          lastDate.toISOString()
          self.chartData.labels = [
            ...dataLabels,
            `${self.momentDMY_HI(lastDate)} ${self.getLabelTimezone()}`,
          ]

          const fuelValues = data.reportFuel.reportFuel.data.map((item) =>
            normalizeFuelValues(
              item.sensors,
              item.fuel,
              item.fuelPercentage,
              item.vehicle?.fuelCapacity,
              this.hasAccess(['b1x-new-fuel-expectation:read'])
            )
          )
          if (this.columns.includes('fuel') && !rangeAxes.isOdo) {
            self.chartData.datasets.push({
              type: 'line',
              label: 'Bahan Bakar (L)',
              reportLabel: 'fuelVolume',
              data: fuelValues.map((item) => item.fuel.value),
              tableValues: fuelValues.map((item) => item.fuel.display),
              unit: 'L',
              borderColor: '#3EFEFF',
              fill: false,
              pointRadius: 0,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: true,
            })
          }
          if (this.columns.includes('fuelPercentage')) {
            self.chartData.datasets.push({
              type: 'line',
              label: 'Bahan Bakar (%)',
              reportLabel: 'fuelPercentage',
              data: fuelValues.map((item) => item.fuelPercentage.value),
              tableValues: fuelValues.map((item) => item.fuelPercentage.display),
              unit: '%',
              borderColor: '#000096',
              fill: false,
              pointRadius: 0,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: true,
            })
          }
          if (this.columns.includes('speed')) {
            self.chartData.datasets.push({
              type: 'bar',
              barThickness: 2,
              label: 'Kecepatan',
              reportLabel: 'speed',
              data: data.reportFuel.reportFuel.data.map((item) => item.speed),
              borderColor: '#009600',
              backgroundColor: '#009600',
            })
          }

          if (self.displaySummaryChart) {
            self.chartData.datasets.push({
              type: 'line',
              label: self.$t('report.fields.fuelAverage'),
              reportLabel: 'averageFuelConsumption',
              data: data.reportFuel.reportFuel.data.map((item) => item.averageFuelConsumption),
              borderColor: '#FF7417',
              fill: false,
              pointRadius: 0,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: true,
            })
          }

          if (this.columns.includes('acc')) {
            self.chartData.datasets.push({
              yAxisID: 'acc',
              label: 'ACC',
              reportLabel: 'acc',
              data: data.reportFuel.reportFuel.data.map(function (item) {
                return item.acc == null ? null : item.acc == 'off' ? 0 : 1
              }),
              borderColor: '#ff000e',
              fill: false,
              pointRadius: 0,
              lineTension: 0,
              borderWidth: 2,
              steppedLine: true,
            })
          }
          if (rangeAxes.axes.length) {
            self.chartData.datasets.forEach((dataset) => {
              if (dataset.yAxisID === 'acc') return
              dataset.yAxisID = (dataset.unit === '%' && rangeAxes.percentageAxisId) || 'numeric'
            })
          }
          if (data.reportFuel.reportFuel.data.length === 0) {
            const dates = this.getDatesInRange(this.filters.periode[0], this.filters.periode[1])
            dates.forEach(function (date) {
              self.chartData.labels.push(`${self.momentDMHms(date)} ${self.getLabelTimezone()}`)
            })
          }
          self.isLoadingGraph = false
          const result = {
            chartData: self.chartData,
            yAxesAdditional: self.yAxesAdditional,
            isLoadingGraph: self.isLoadingGraph,
          }
          return result
        },
      }
    },
    periodeLabel() {
      return this.filters.periode
        ? this.momentDatetimeShortMonth(this.filters.periode[0]) +
            ' - ' +
            this.momentDatetimeShortMonth(this.filters.periode[1])
        : '-'
    },
    licensePlateLabel() {
      if (!Array.isArray(this.filters.vehicleId) && this.filters.vehicleId) {
        return this.filterDetail != {} ? this.filterDetail.licensePlate : '-'
      } else if (
        Array.isArray(this.filters.vehicleId) &&
        this.filters.vehicleId &&
        this.filters.vehicleId.length > 0
      ) {
        return this.filterDetail != {} ? this.filterDetail.licensePlate : '-'
      } else {
        return '-'
      }
    },
    vehicleIdCount() {
      return this.filters.vehicleId &&
        Array.isArray(this.filters.vehicleId) &&
        this.filters.vehicleId.length > 0
        ? this.filters.vehicleId.length
        : 0
    },
    savedReport() {
      return JSON.parse(localStorage.getItem('saveReports'))
    },
    report() {
      const reports = [...reportList].concat(this.savedReport)
      return reports.find((item) => item.key == this.reportKey)
    },
    filterValue() {
      return this.$store.state.report.filterValue
    },
    hasCsvScope() {
      return this.report.downloadOptions?.includes('csv') && this.hasAccess(['csv_report:read'])
    },
    itemDownloadDropdown() {
      let list = []
      if (this.isShowGraph) {
        list = list.concat([
          {
            name: 'excel',
            label: this.$t('global.action.downloadExcel'),
            onClick: () => {
              this.downloadReport('xlsx')
            },
            icon: this.excelIcon,
          },
          {
            name: 'pdf',
            label: this.$t('global.action.downloadPdf'),
            onClick: () => {
              this.downloadReport('pdf')
            },
            icon: this.pdfIcon,
          },
        ])
      }
      if (this.hasCsvScope) {
        list.push({
          name: 'csv',
          label: this.$t('global.action.downloadCsv'),
          onClick: () => {
            this.downloadReport('csv')
          },
          icon: this.excelIcon,
        })
      }
      if ((this.isShowGraph ?? true) && this.chartData.datasets.length > 1) {
        list.push({
          name: 'graph',
          label: this.$t('global.action.downloadGraphicPdf'),
          onClick: () => {
            this.downloadReport('graph')
          },
          icon: this.pdfIcon,
        })
      }
      return list
    },
    hasEmail() {
      return this.emails && this.emails.length > 0
    },
    getActiveDataSet() {
      return {
        labels: () => {
          const dataSet = this.chartData.datasets.map((item) => {
            return item._meta
              ? Object.keys(item._meta)
                ? { hidden: item._meta[Object.keys(item._meta)[0]]?.hidden ?? false }
                : { hidden: false }
              : { hidden: false }
          })
          const finalDataSet = dataSet.map((item) => item.hidden ?? false)
          const getDataSet = []
          const chartMapping = this.chartData.datasets.map((dataset) => dataset.reportLabel)
          finalDataSet.forEach((item, index) => {
            if (!item && chartMapping[index]) {
              getDataSet.push(chartMapping[index])
            }
          })
          return getDataSet
        },
      }
    },
  },
  mounted() {
    const emailStorage = localStorage.getItem('emailReceiverReport')
    if (emailStorage) {
      this.recipientsEmail = emailStorage.split(',')
    }

    this.reportKey =
      this.$route.query.key && this.savedReport.find((item) => item.key === this.$route.query.key)
        ? this.$route.query.key
        : this.reportKey
    const { reportFilter, reportFilterValue } = this.getFilterReport(this.reportKey)
    this.$store.commit('report/updateFilter', reportFilter)
    this.$store.commit('report/updateFilterValue', reportFilterValue)
    this.filters = { ...this.$store.state.report.filterReport }
    this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
    this.isMounted = true
    this.track('View ' + this.report.event)
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
  },
  methods: {
    getFuelSensor(sensors) {
      return getFuelSensorString(sensors, 'L')
    },
    getFuelValue(data) {
      return normalizeFuelValues(
        data.sensors,
        data.fuel,
        data.fuelPercentage,
        data.vehicle?.fuelCapacity,
        this.hasAccess(['b1x-new-fuel-expectation:read'])
      ).fuel.display
    },
    getFuelPercentage(data) {
      return normalizeFuelValues(
        data.sensors,
        data.fuel,
        data.fuelPercentage,
        data.vehicle?.fuelCapacity,
        this.hasAccess(['b1x-new-fuel-expectation:read'])
      ).fuelPercentage.display
    },
    getBaseFilter(filters) {
      if (!filters || Object.keys(filters).length <= 0) return {}
      const result = {}
      Object.keys(filters).forEach((item) => {
        if (
          filters[item] !== null &&
          ((Array.isArray(filters[item]) && filters[item].length > 0) ||
            (!Array.isArray(filters[item]) && filters[item]))
        ) {
          result[item] = filters[item]
        }
      })
      const { periode, interval, ...otherFilter } = result
      const startDate = periode.length > 0 ? moment(periode[0]).toISOString() : null
      const endDate = periode.length > 0 ? moment(periode[1]).toISOString() : null
      let intervalData = interval
      if (!this.hasAccess(['interval_report:read'])) {
        intervalData = undefined
      }
      const model = {
        startDate,
        endDate,
        interval: intervalData,
        ...otherFilter,
        disabledOdoFuel: this.hasAccess(['b1x-new-fuel-expectation:read']),
      }
      return model
    },
    handleTableChanged(isTable) {
      if (!isTable) {
        this.track(`View ${this.report.event} Chart`)
      } else {
        this.track('View ' + this.report.event)
        if (!this.mediaQuery.matches) {
          this.$store.dispatch('layout/toggleMenu', true)
        }
      }
    },
    handleColumnReport(fields) {
      this.fuelRangeAxes = []
      this.reportFields = fields
      this.tableKey += 1
      this.summaryKey += 1
    },
    forceRerender() {
      this.fuelRangeAxes = []
      this.filters = { ...this.$store.state.report.filterReport }
      this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
      this.tableKey += 1
      this.summaryKey += 1
      this.isBusySummary = true
    },
    showModal() {
      this.$refs['modal-filter'].SetFormValues(this.filterValue)
      this.$bvModal.show(this.report.modal)
    },
    redirectToMap(lat, long) {
      let url = `https://www.google.com/maps/search/?api=1&query=${lat},${long}`
      window.open(url, '_blank')
    },
    handleEmails(emails) {
      this.emails = emails
      this.recipientsEmail = emails
      this.isSubmitEmail = true
      if (emails.length > 0) {
        localStorage.setItem('emailReceiverReport', emails.join())
      }
      this.$bvModal.hide(this.idEmailModal)
      this.$mcToast.info(this.$t('report.text.popupEmailInfo'))
      this.downloadReport(this.reportAfterEmail)
    },
    showDownloader(periode, extension, result) {
      const downloadProgress = {
        status: 'I',
        url: null,
        name:
          `${extension === 'graph' ? 'Grafik ' : ''}${this.title} ` +
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY') +
          `.${extension === 'graph' ? 'pdf' : extension}`,
        periode:
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY'),
        downloadedAt: moment().format('DD MMM YYYY HH:mm:ss'),
        type: extension === 'graph' ? 'pdf' : extension,
        id: result.fuelReport.id,
      }
      if (result.isSuccessful) {
        this.$store.commit('report/doneProgress', downloadProgress)
      } else {
        downloadProgress.status = 'F'
        this.$store.commit('report/failProgress', downloadProgress)
      }
      this.$store.commit('report/showDownloadPopup', true)
    },
    downloadReport(extension, contact) {
      if (
        !this.isSubmitEmail &&
        extension !== 'graph' &&
        this.actualTotal > this.totalRows &&
        (!contact || contact.length <= 0)
      ) {
        this.$bvModal.show(this.idEmailModal)
        this.reportAfterEmail = extension
        return
      }
      this.isSubmitEmail = false

      const fileType = this.trackDownloadReport(this.report, extension, contact)

      const model = this.filterReport
      if (!model) {
        setTimeout(() => {
          this.showModal()
        }, 1000)
        return false
      }
      const { periode } = this.filters
      const { summaryChart, ...remainingModel } = model

      let filterVar = {
        ...remainingModel,
        subtitle: `Periode : ${this.momentDateLongMonth(periode[0])} - ${this.momentDateLongMonth(
          periode[1]
        )}`,
        downloadAs: fileType,
        emails: this.emails,
      }

      if (summaryChart) {
        filterVar.withSummary = true
      }

      if (extension === 'graph') {
        filterVar = {
          ...filterVar,
          column: this.allColumn.filter((field) => field != 'address' && field !== 'geofence'),
          labels: this.getActiveDataSet.labels(),
          ...(summaryChart && { chartAddons: ['averageFuelConsumption'] }),
        }
      }

      if (this.report.labelName) {
        filterVar.title = this.report.labelName
      }

      if (contact) {
        filterVar = {
          ...filterVar,
          isShared: true,
          contact: contact,
        }
      }

      this.$apollo
        .query({
          query: FUEL_REPORT,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          let isSuccessful = res.data.fuelReport.isSuccessful
          if (!this.hasEmail && !contact) {
            this.showDownloader(periode, extension, res.data.fuelReport)
          } else if (contact) {
            if (isSuccessful) this.$mcToast.success(this.$t('report.text.sharedSuccess'))
            else this.$mcToast.error(this.$t('report.text.sharedFailed'))
          }
        })
    },
  },
}
</script>
